import { useTbt } from '@melio/form-controls';
import { Container, Loader, useToast } from '@melio/penny';
import { CardParams, useFundingSources } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useNavigate } from '@melio/platform-utils';
import { usePlan, useSubscriptionMe, useSubscriptionsPreview } from '@melio/subscriptions-api';
import { useSubscriptionOnboarding, useSubscriptionSuccessModal } from '@melio/subscriptions-sdk';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_DASHBOARD_ROUTE } from '../../constants';
import { useSubscriptionRouter } from '../../utils/useSubscriptionRouter';
import { CardFundingSourceFormFields } from './components/CardFundingSourceForm/types';
import { SubscriptionCheckoutScreen, SubscriptionCheckoutScreenProps } from './screens/SubscriptionCheckout.screen';

export type SubscriptionCheckoutActivityProps = {
  returnUrl?: string;
  planId: string;
};

export const SubscriptionCheckoutActivity = ({ returnUrl, planId }: SubscriptionCheckoutActivityProps) => {
  const { data: plan, isFetching: isPlanFetching } = usePlan({ params: { id: planId } });
  const { create: createSubscription, isMutating: isPlanSaving } = useSubscriptionMe({ enabled: false });
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { data: preview, isFetching: isPreviewFetching } = useSubscriptionsPreview({
    id: planId,
    enabled: isPlatformSubscriptionsEnabled,
  });
  const { showSuccessModal } = useSubscriptionSuccessModal();
  const { completeOnboarding } = useSubscriptionOnboarding();

  const { formatMessage } = useMelioIntl();
  const navigate = useNavigate();
  const { goToCheckoutAddPlaidAccount } = useSubscriptionRouter();
  const {
    data: fundingSources,
    isFetching: isFetchingFundingSources,
    verifyCard,
    create,
    refetch,
  } = useFundingSources();
  const [isVerifyingCard, setIsVerifyingCard] = useState(false);
  const [searchParams] = useSearchParams();
  const [newFundingSourceId, setNewFundingSourceId] = useState<string | undefined>(
    searchParams.get('fundingSourceId') ?? undefined
  );
  const { bt, tokenize } = useTbt();
  const toast = useToast();

  const createNewFundingSourceAndRefetch = async (fields: CardFundingSourceFormFields, cardParams?: CardParams) => {
    if (cardParams) {
      const { firstName, lastName, postalCode, state, line1, city } = fields;
      const address = { postalCode, state, line1, city };
      const details = { ...cardParams, cardOwner: { firstName, lastName }, address };
      const result = await create({ type: 'card', details });
      setNewFundingSourceId(result.id);

      await refetch();
    }
  };
  const onCreateCardDetailsDone: SubscriptionCheckoutScreenProps['onCreateCardDetailsDone'] = async (
    fields: CardFundingSourceFormFields
  ) => {
    if (bt) {
      try {
        const { cardNumber, cardExpiration, cardVerificationCode } = fields;
        setIsVerifyingCard(true);
        const cardParams = await tokenize({
          cardNumber,
          cardExpiration,
          cardVerificationCode,
        });
        try {
          await verifyCard({
            cardBin: cardParams.cardBin,
            tabapayToken: cardParams.tabapayToken,
            tokenProvider: 'basistheory',
          });
          await createNewFundingSourceAndRefetch(fields, cardParams);
        } catch (error) {
          toast({
            type: 'error',
            title: formatMessage('activities.subscription.checkout.addCardFailed'),
          });
        } finally {
          setIsVerifyingCard(false);
        }
      } catch (error) {
        toast({
          type: 'error',
          title: formatMessage('activities.subscription.checkout.addCardFailed'),
        });
        setIsVerifyingCard(false);
      }
    }
  };

  const onSubmit = () => {
    void createSubscription({ fundingSourceId: '-1', planId, numberOfSeats: -1 })
      .then(() => {
        navigate(returnUrl || DEFAULT_DASHBOARD_ROUTE);
        showSuccessModal({ onClose: completeOnboarding });
      })
      .catch(() =>
        toast({ type: 'error', title: formatMessage('activities.subscription.checkout.summary.toast.error') })
      );
  };

  if (isPreviewFetching || isPlanFetching || !preview || !plan) {
    return (
      <Container paddingY="xxxl" data-testid="subscription-checkout-screen-loader">
        <Loader />
      </Container>
    );
  }

  return (
    <SubscriptionCheckoutScreen
      preview={preview}
      plan={plan}
      onSubmit={onSubmit}
      isPlanSaving={isPlanSaving}
      fundingSources={fundingSources}
      newFundingSourceId={newFundingSourceId}
      isLoading={isFetchingFundingSources || isVerifyingCard}
      onCreateCardDetailsDone={onCreateCardDetailsDone}
      onAddBankAccountClicked={goToCheckoutAddPlaidAccount}
    />
  );
};
